import {FormikProvider, FormikValues, useFormik} from 'formik';
import {get, set, every, forEach, isEmpty, trim, cloneDeep, omit, find, some} from 'lodash';
import {useState, useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmModal, InputFormik, notifier} from 'components';
import {TRANSLATION_KEYS} from 'locale';
import {UserPermissions, RolePermission, FormattedPermission, ApiError} from 'types';
import {apiUtils} from 'utils';

import {EditablePermissionList} from '../EditablePermissionList';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.ROLES.MODAL;
const NOTIFIER_ERROR_KEY = TRANSLATION_KEYS.UI_KIT.NOTIFIER.PREDEFINED.SAVE.ERROR;

type Props = {
    open: boolean;
    role?: {
        id?: string;
        name: string;
        permissions: UserPermissions
    };
    error?: ApiError;
    editMode?: boolean;
    availablePermissions: UserPermissions;
    getFormattedPermissions: (permissions: UserPermissions) => FormattedPermission[];
    onClose: (value: boolean, data: RolePermission) => Promise<void>;
};

export const RoleModal: React.FC<Props> = ({
    open, role, editMode, error, availablePermissions, getFormattedPermissions, onClose
}: Props) => {
    const {t} = useTranslation();
    const [updatedPermissions, setUpdatedPermissions] = useState({});
    const title = editMode ? T_KEYS.TITLE_EDIT : T_KEYS.TITLE_ADD;

    const formattedPermissions = useMemo(() =>
        getFormattedPermissions(availablePermissions),
    [availablePermissions, getFormattedPermissions]);

    const validateForm = useCallback((formValues: FormikValues) => {
        const errors = {};
        if (!trim(formValues.name)) {
            set(errors, 'name', t(T_KEYS.ROLE_INPUT_EMPTY));
        }
        return errors;
    }, [t]);

    const formik = useFormik({
        initialValues: {
            name: ''
        },
        onSubmit: (formValues: {name: string}, {setFieldError}) => {
            const errors = validateForm(formValues);
            if (!isEmpty(errors)) {
                forEach(errors, (errorMessage, errorKey) => {
                    setFieldError(errorKey, errorMessage);
                });
                notifier.error({title: t(NOTIFIER_ERROR_KEY)});
            } else {
                onClose(true, {
                    id: role.id,
                    name: trim(formValues.name),
                    permissions: updatedPermissions
                });
            }
        }
    });
    const {submitForm, setFieldValue, resetForm, errors, setFieldError} = formik;

    useEffect(() => {
        if (open) {
            resetForm();
            setFieldValue('name', role?.name);
            setUpdatedPermissions(cloneDeep(role?.permissions));
        }
    }, [role, open, resetForm, setFieldValue]);

    useEffect(() => {
        const err = apiUtils.getInputValidationErrorProps(error, t, 'name');
        if (err.helperText) {
            setFieldError('name', err.helperText);
        }
    }, [error, t, setFieldError]);

    //TODO: should be rewrite to optimal variant (parent is intermediate when it has min one checked and min one unchecked child
    //      parent is selected when it has all children checked)
    const checkSelectedPermissions = useCallback(
        (path: string, selected: boolean[], permissions: UserPermissions, getResult: (selected: boolean[]) => boolean) => {
            const availableChildren = getFormattedPermissions(get(availablePermissions, path));
            const selectedChildren = getFormattedPermissions(get(permissions, path));
            forEach(availableChildren, (availableChild) => {
                const selectedChild = find(selectedChildren, {path: availableChild.path});
                if (!isEmpty(availableChild?.children)) {
                    if (selectedChild?.enabled) {
                        forEach(availableChild?.children, () => selected.push(true));
                    } else {
                        return checkSelectedPermissions(path.concat('.', availableChild.name), selected, permissions, getResult);
                    }
                } else {
                    selected.push(selectedChild?.enabled);
                }
            });
            return getResult(selected);
        }, [availablePermissions, getFormattedPermissions]);

    const isParentIntermediate = useCallback((path: string, permissions: UserPermissions) => {
        const getResult = (selectedValues: boolean[]) => some(selectedValues, (item) => !!item) && !every(selectedValues, (item) => !!item);
        return checkSelectedPermissions(path, [], permissions, getResult);
    }, [checkSelectedPermissions]);

    const isParentSelected = useCallback((path: string, permissions: UserPermissions) => {
        if (get(permissions, path)?.enabled) {
            return true;
        }
        const getResult = (selectedValues: boolean[]) => every(selectedValues, (item) => !!item);
        return checkSelectedPermissions(path, [], permissions, getResult);
    }, [checkSelectedPermissions]);

    const getPermissionParentPath = useCallback((path: string) => path.substring(0, path.lastIndexOf('.')), []);

    const isItemSelected = useCallback((path: string, permissions: UserPermissions) =>
        get(permissions, path)?.enabled,
    []);

    const getChildren = useCallback((path: string, permissions: UserPermissions) => omit(get(permissions, path), 'enabled'), []);

    const onToggleItem = useCallback((path: string, isParent: boolean) => {
        let clone = {...updatedPermissions};
        if (get(clone, path)) {
            if (isParent) {
                set(clone, path, null);
            } else {
                clone = omit(clone, path);
                const parentPath = getPermissionParentPath(path);
                const children = getChildren(parentPath, clone);
                if (isEmpty(children)) {
                    clone = omit(clone, parentPath);
                }
            }
        } else {
            const permission = get(availablePermissions, path);
            set(clone, path, permission);
        }
        setUpdatedPermissions(clone);
    }, [availablePermissions, updatedPermissions, getChildren, getPermissionParentPath]);

    const onModalClose = useCallback((confirm: boolean) => {
        if (confirm) {
            submitForm();
        } else {
            onClose(false, null);
        }

    }, [onClose, submitForm]);

    const nothingSelected = useMemo(() =>
        isEmpty(updatedPermissions) || every(updatedPermissions, (value) => isEmpty(value)), [updatedPermissions]);

    return (
        <ConfirmModal
            title={t(title)}
            maxWidth='sm'
            fullWidth
            open={open}
            okDisabled={nothingSelected}
            okText={t(T_KEYS.BUTTON_SAVE)}
            onClose={onModalClose}
        >
            {error && !errors.name &&
                <div className='tw-text-alert'>
                    {t(T_KEYS.ERROR_MESSAGE)}
                </div>}
            <FormikProvider value={formik}>
                <InputFormik
                    required
                    type='text'
                    name='name'
                    className='tw-mb-4'
                    label={t(T_KEYS.ROLE_INPUT)}
                />
            </FormikProvider>

            <EditablePermissionList
                selectedPermissions={updatedPermissions}
                formattedPermissions={formattedPermissions}
                availablePermissions={availablePermissions}
                isParentIntermediate={isParentIntermediate}
                isParentSelected={isParentSelected}
                isItemSelected={isItemSelected}
                onToggleItem={onToggleItem}
            />

        </ConfirmModal>
    );
};
